import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Card,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Typography,
  Box,
  CircularProgress,
} from "@mui/material";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { DemoContainer } from "@mui/x-date-pickers/internals/demo";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { useFormik } from "formik";
import * as Yup from "yup";
import dayjs from "dayjs";
import InputDropdown from "../../../components/InputFields/InputDropdown";
import InputText from "../../../components/InputFields/InputText";
import sendToast from "../../../components/Toast/sendToast";
import { formatUSPhone } from "../../../utils/usFormat";
import AxiosInstance from "../../AxiosInstance";
import CardPayment from "./Payment/CardPayment";

import { Button } from "reactstrap";
import { Grid } from "@mui/material";
import { Col } from "react-bootstrap";
import { LoaderComponent } from "../../../components/Icon/Index";
import showToast from "../../../components/Toast/Toster";
import WhiteButton from "../../../components/Button/WhiteButton";
import BlueButton from "../../../components/Button/BlueButton";
import { setItem } from "../../../utils/storage";
import AppLogo from "../../../assets/image/CMS_LOGO.svg";
import Payment from "../../../assets/image/Payment.png";
import ContactImage from "../../../assets/White-sidebar-icon/MOBILE.svg";
import CustomerEmail from "../../../assets/White-sidebar-icon/EMAIL.svg";
import DollerInput from "../../../components/InputFields/Doller";
import ApplePayPayment from "./Payment/ApplePayPayment";
import GooglePayPayment from "./Payment/GooglePayPayment";
import PayaAddCardForm from "../../../components/PayaAddCard/PayaAddCardForm";
const InvoiceLivePayment = () => {
  const queryParams = new URLSearchParams(window.location.search);
  const { token } = { token: queryParams?.get("token") };
  const [invoiceData, setInvoiceData] = useState();
  const [selectedCard, setSelectedCard] = useState(undefined);
  const [loader, setLoader] = useState(true);
  const [error, setError] = useState("");
  const [tokenData, setTokenData] = useState();
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [invoiceCode, setInvoiceCode] = useState("");
  const [invoiceCodeLoading, setInvoiceCodeLoading] = useState(false);
  const navigate = useNavigate();
  const [cardFeeConfig, setCardFeeConfig] = useState(null);

  const handleTokenSubmit = async () => {
    if (!invoiceCode) {
      setError("Please enter an invoice code");
      return;
    }
    try {
      setInvoiceCodeLoading(true);
      const response = await AxiosInstance.get(
        `/v1/invoice/get-invoice-code/${invoiceCode}`
      );
      if (response?.data?.statusCode === 200) {
        navigate(`/InvoicePayment?token=${response?.data?.token}`);
        setItem("customerToken", response?.data?.Logintoken);
        setItem("CustomerId", response?.data?.CustomerId);
      } else if (response?.data?.statusCode === 203) {
        showToast.error(response?.data?.message);
        return;
      } else {
        setError("No token received from the API");
      }
    } catch (error) {
      setError(
        error?.response?.data?.message || "Error fetching invoice token"
      );
      console.error("Error fetching invoice token:", error);
    } finally {
      setInvoiceCodeLoading(false);
    }
  };

  useEffect(() => {
    const validateToken = async () => {
      if (!token) {
        setLoader(false);
        return;
      }

      try {
        setLoader(true);
        const response = await AxiosInstance.get(`/v1/invoice/InvoicePayment`, {
          params: { token },
        });
        
        if (response?.data?.statusCode === 200) {
          const { data } = response?.data;
          setTokenData({
            invoice: data?.invoice,
            company: data?.company,
            customer: data?.customer,
            customerProfile: data?.customerProfile,
            companyProfile: data?.companyProfile,
            gatewayKeys: data?.gatewayKeys?.gatewayType || data?.gatewayKeys,
            creditCardEnabled: data?.creditCardEnabled,
            achEnabled: data?.achEnabled,
          });

          // Set card fee config from backend response
          if (data?.cardFeeConfig) {
            setCardFeeConfig(data.cardFeeConfig);
          } else {
            // Default to Cash Discount if no config found
            setCardFeeConfig({
              PricingStructure: "Cash Discount",
              FeeLabel: "",
              FeeType: "",
              FeeAmount: 0,
            });
          }
          
          // Set invoice data directly from token validation response
          // invoiceAccount is now calculated in the backend, so no second API call needed
          const initialInvoiceData = {
            ...data?.invoice,
            InvoiceId: data?.invoice?.InvoiceId,
            InvoiceNumber: data?.invoice?.InvoiceNumber,
            Subject: data?.invoice?.Subject,
            Total: data?.invoice?.Total,
            invoiceAccount: data?.invoice?.invoiceAccount,
            location: data?.invoice?.location || data?.locationData,
            CompanyData: {
              CompanyUrl: data?.company?.CompanyId,
              PhoneNumber: data?.company?.PhoneNumber,
              EmailAddress: data?.company?.EmailAddress,
            },
            customer: {
              FirstName: data?.customer?.FirstName,
              LastName: data?.customer?.LastName,
              EmailAddress: data?.customer?.EmailAddress,
              TransactionFee: data?.customerProfile?.TransactionFee,
            },
          };
          setInvoiceData(initialInvoiceData);
          
          // Fetch full invoice data including payment history immediately
          if (data?.invoice?.InvoiceId) {
            // Call fetchData directly with invoiceId and tokenData to avoid state dependency issues
            try {
              const tempTokenData = {
                invoice: data?.invoice,
                company: data?.company,
                customer: data?.customer,
                customerProfile: data?.customerProfile,
                companyProfile: data?.companyProfile,
                gatewayKeys: data?.gatewayKeys?.gatewayType || data?.gatewayKeys,
              };
              await fetchFullInvoiceData(data?.invoice?.InvoiceId, initialInvoiceData, tempTokenData);
            } catch (fetchError) {
              console.error("Error fetching full invoice data:", fetchError);
              // Continue even if fetch fails, we have initial data
            }
          }
        } else {
          setError(
            response?.data?.message || "Failed to validate payment token"
          );
        }
      } catch (err) {
        console.error("Token validation error:", err);
        const errorMessage =
          err?.response?.data?.message || "Failed to validate payment token";
        setError(errorMessage);

        if (err?.response?.status === 401) {
          setTimeout(() => {
            navigate("/error", {
              state: {
                message:
                  "Your payment link has expired or is invalid. Please request a new payment link.",
                code: 401,
              },
            });
          }, 3000);
        }
      } finally {
        setLoader(false);
      }
    };

    if (token) {
    validateToken();
    } else {
      setLoader(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);
  const [buttonLoader, setButtonLoader] = useState(false);
  const sendMail = async () => {
    try {
      setButtonLoader(true);
      const mailRes = await AxiosInstance.post(
        `/v1/invoice-payment/send_mail/${tokenData?.invoice?.InvoiceId}`
      );
      if (mailRes?.data?.statusCode === 200) {
        if (mailRes?.data?.emailNotSent) {
          showToast.warning(mailRes?.data?.message || "No email was sent.");
        } else {
          showToast.success(mailRes?.data?.message);
        }
      } else if (mailRes?.data?.statusCode === 209) {
        showToast.warning(mailRes?.data?.message || "You have reached the email limit");
      } else {
        sendToast(mailRes?.data?.message || "Failed to send email");
      }
    } catch (error) {
      if (error?.response?.status === 209 || error?.response?.data?.statusCode === 209) {
        showToast.warning(error?.response?.data?.message || "You have reached the email limit");
      } else {
        sendToast(error?.response?.data?.message || "Failed to send email");
      }
    } finally {
      setSentMail(false);
      setButtonLoader(false);
    }
  };

  const [paymentMethod, setPaymentMethod] = useState("Apple Pay");
  const [sentMail, setSentMail] = useState(false);
  const paymentFormik = useFormik({
    enableReinitialize: true,
    initialValues: {
      method: "Apple Pay",
      amount: invoiceData?.invoiceAccount || invoiceData?.Total || "",
      date: dayjs(new Date()).format("MM/DD/YYYY"),
      customer_vault_id: "",
      billing_id: "",
      cheque_number: "",
      token: "",
    },
    validationSchema: Yup.object({
      method: Yup.string().required("This field is required."),
      // token: Yup.string().when("method", {
      //   is: "Credit/debit card",
      //   then: (schema) => schema.required("Please add a payment card"),
      //   otherwise: (schema) => schema.notRequired(),
      // }),
      cheque_number: Yup.string().when("method", {
        is: "Check",
        then: (schema) => schema.required("This field is required."),
        otherwise: (schema) => schema.notRequired(),
      }),
      amount: Yup.number()
        .required("This field is required.")
        .min(1, "The amount must be at least 1.")
        .max(
          invoiceData?.invoiceAccount,
          `The amount must be less than or equal to ${invoiceData?.invoiceAccount}.`
        ),
      // billing_id: Yup.string().when("method", {
      //   is: "Credit/debit card",
      //   then: (schema) => schema.required("Please select a card"),
      //   otherwise: (schema) => schema.notRequired(),
      // }),
    }),
    onSubmit: async (values) => {
      setButtonLoader(true);
      setError("");

      try {
        if (!values?.date) {
          setError("Please select date");
          setButtonLoader(false);
          return;
        }

        if (paymentMethod === "Credit/debit card" && !values?.billing_id && !values?.token) {
          setError("Please select card");
          setButtonLoader(false);
          return;
        }

        // Calculate totals with fee using Cash Discount/Surcharge logic
        const totals = calculatePaymentTotals(values?.amount, paymentMethod);
        // If TransactionFee is disabled, use original amount; otherwise use finalTotal (which includes fee)
        const finalAmount =
          paymentMethod === "Credit/debit card"
            ? invoiceData?.customer?.TransactionFee
              ? totals.finalTotal
              : totals.cashTotal
            : totals.cashTotal;
        const convenienceFee = totals.convenienceFee;

        let response;
        if (paymentMethod === "Credit/debit card") {
          const isDejavoo = (tokenData?.gatewayKeys || "").toString().toUpperCase() === "DEJAVOO";
          const cardToken = values?.token || selectedCard?.token;
          const billingId = values?.billing_id != null && typeof values.billing_id === "string" && values.billing_id.trim() !== "" && !/^\d+$/.test(String(values.billing_id).trim()) ? values.billing_id : Number(values?.billing_id);
          const customerVaultId = values?.customer_vault_id != null && typeof values.customer_vault_id === "string" && values.customer_vault_id.trim() !== "" && !/^\d+$/.test(String(values.customer_vault_id).trim()) ? values.customer_vault_id : Number(values?.customer_vault_id);
          const object = {
            paymentData: {
              billing_id: billingId,
              customer_vault_id: customerVaultId,
              date: values?.date,
              company: tokenData?.company?.CompanyUrl,
              amount: parseFloat(values?.amount), // Original amount without transaction fee
              originalAmount: parseFloat(values?.amount), // Original amount before fee
              actualAmount: finalAmount, // Amount with fee
              CompanyId: tokenData?.company?.CompanyId,
              Total: totals.finalTotal,
              CustomerId: invoiceData?.CustomerId,
              InvoiceId: invoiceData?.InvoiceId,
              paymentMethod: "card_on_file",
              paymentDoingFor: "Invoice",
              paymentSource: "Invoice Live Payment",
              method: "Card",
              token: values?.token,
              ...(isDejavoo && cardToken ? { paymentToken: cardToken } : {}),
              CreatedFrom: "Invoice-Payment",
              // Only include fee fields if customer has TransactionFee enabled
              ...(invoiceData?.customer?.TransactionFee && convenienceFee > 0
                ? {
                    convenienceFee: convenienceFee,
                    transactionFee: convenienceFee, // Also send as transactionFee for display
                    transactionFeeDetails: {
                      feeAmount: convenienceFee,
                      transactionFee: convenienceFee,
                      originalAmount: parseFloat(values?.amount),
                      totalAmount: finalAmount,
                    },
                  }
                : {}),
            },
          };

          response = await AxiosInstance.post(
            "/v1/payment/process-payment",
            object
          );
        } else if (paymentMethod === "Apple Pay") {
          const object = {
            paymentDetails: {
              date: values?.date,
              company: tokenData?.company?.CompanyUrl,
              amount: values?.amount,
              CompanyId: tokenData?.company?.CompanyId,
              Total: invoiceData?.Total,
              CustomerId: invoiceData?.CustomerId,
              InvoiceId: invoiceData?.InvoiceId,
              method: "Apple Pay",
            },
          };

          response = await AxiosInstance.post(
            "/v1/nmi/apple-pay-payment",
            object
          );
        } else if (paymentMethod === "Google Pay") {
          const object = {
            paymentDetails: {
              date: values?.date,
              company: tokenData?.company?.CompanyUrl,
              amount: values?.amount,
              CompanyId: tokenData?.company?.CompanyId,
              Total: invoiceData?.Total,
              CustomerId: invoiceData?.CustomerId,
              InvoiceId: invoiceData?.InvoiceId,
              method: "Google Pay",
            },
          };

          response = await AxiosInstance.post(
            "/v1/nmi/google-pay-payment",
            object
          );
        }

        if (response?.data?.statusCode === 100 || response?.data?.success === true) {
          const msg = response?.data?.message || response?.data?.data?.responseMessage || response?.data?.data?.responsetext || "";
          showToast.success(msg || "");
          // Clear button loader immediately so it never stays stuck
          setButtonLoader(false);
          if (sentMail) {
            try {
              await sendMail();
            } catch (mailError) {
              console.error("Error sending mail after payment:", mailError);
            }
          }
          // Show overlay loader while refreshing data after payment
          setIsRefreshing(true);
          try {
            await new Promise(resolve => setTimeout(resolve, 300));
            await fetchData();
          } catch (error) {
            console.error("Error refreshing data after payment:", error);
          } finally {
            setIsRefreshing(false);
          }
        } else {
          const msg = response?.data?.message || response?.data?.data?.responseMessage || response?.data?.data?.responsetext || response?.data?.error || "";
          sendToast(msg || "");
          setButtonLoader(false);
        }
      } catch (error) {
        const errMsg = error?.response?.data?.message || error?.response?.data?.data?.responseMessage || error?.response?.data?.data?.responsetext || error?.response?.data?.error || "";
        setError(errMsg || "");
        sendToast(errMsg || "");
        setButtonLoader(false);
      } finally {
        setButtonLoader(false);
      }
    },
  });

  const fetchFullInvoiceData = async (invoiceId, initialData = null, providedTokenData = null) => {
    try {
      if (!invoiceId) {
        console.error("No invoiceId provided to fetchFullInvoiceData");
        return;
      }
      
      // Use provided tokenData or fall back to state tokenData
      const currentTokenData = providedTokenData || tokenData;
      
      const invoiceRes = await AxiosInstance.get(
        `/v1/invoice-payment/${invoiceId}`
      );
      if (invoiceRes?.status === 200) {
        const updatedData = invoiceRes?.data?.invoiceData;
        setInvoiceData((prevData) => {
          // Use the most recent data available
          const currentData = prevData || initialData;
          
          // Use updatedData.invoiceAccount directly, even if it's 0 or "0.00"
          // This is the actual remaining balance from the API
          const invoiceAccount = updatedData?.invoiceAccount !== undefined 
            ? updatedData.invoiceAccount 
            : (currentData?.invoiceAccount !== undefined ? currentData.invoiceAccount : (currentTokenData?.invoice?.Total || 0));
          
          const newInvoiceData = {
            ...currentData,
            ...updatedData,
            InvoiceId: updatedData?.InvoiceId || currentData?.InvoiceId || invoiceId,
            InvoiceNumber: updatedData?.InvoiceNumber || currentData?.InvoiceNumber || currentTokenData?.invoice?.InvoiceNumber,
            Subject: updatedData?.Subject || currentData?.Subject || currentTokenData?.invoice?.Subject,
            Total: updatedData?.Total || currentData?.Total || currentTokenData?.invoice?.Total,
            invoiceAccount: invoiceAccount,
            customerAccount:
              updatedData?.customerAccount !== undefined
                ? updatedData.customerAccount
                : (currentData?.customerAccount !== undefined ? currentData.customerAccount : (currentTokenData?.invoice?.Ledger?.Balance || 0)),
            location: updatedData?.location || currentData?.location || currentTokenData?.invoice?.location,
            paymentHistory: updatedData?.paymentHistory || currentData?.paymentHistory || [],
            // Preserve customer data if it exists
            customer: updatedData?.customer || currentData?.customer || {
              FirstName: currentTokenData?.customer?.FirstName,
              LastName: currentTokenData?.customer?.LastName,
              EmailAddress: currentTokenData?.customer?.EmailAddress,
              TransactionFee: currentTokenData?.customerProfile?.TransactionFee,
            },
            // Preserve CompanyData if it exists
            CompanyData: currentData?.CompanyData || {
              CompanyUrl: currentTokenData?.company?.CompanyId,
              PhoneNumber: currentTokenData?.company?.PhoneNumber,
              EmailAddress: currentTokenData?.company?.EmailAddress,
            },
          };
          
          // Update formik amount after state update (only if invoiceAccount > 0)
          const amountToSet = parseFloat(invoiceAccount) > 0 ? invoiceAccount : (currentData?.Total || currentTokenData?.invoice?.Total || 0);
          if (amountToSet && parseFloat(amountToSet) > 0) {
            setTimeout(() => {
              paymentFormik.setFieldValue("amount", amountToSet);
            }, 0);
          }
          
          return newInvoiceData;
        });
      }
    } catch (error) {
      console.error("Error fetching invoice data:", error);
      showToast.error("Failed to fetch updated invoice data");
      throw error; // Re-throw so caller knows it failed
    }
  };

  const fetchData = async () => {
    try {
      const invoiceId = tokenData?.invoice?.InvoiceId || invoiceData?.InvoiceId;
      if (invoiceId) {
        await fetchFullInvoiceData(invoiceId, invoiceData);
      }
    } catch (error) {
      console.error("Error fetching invoice data:", error);
      showToast.error("Failed to fetch updated invoice data");
      throw error; // Re-throw so caller knows it failed
    }
  };

  // Note: fetchData is now called immediately after token validation
  // This useEffect is kept for refreshing data after payments or when paymentHistory is missing
  useEffect(() => {
    // Only fetch if we have tokenData but no paymentHistory yet
    // This prevents duplicate calls since fetchFullInvoiceData is already called in validateToken
    if (tokenData?.invoice?.InvoiceId && !invoiceData?.paymentHistory && !loader) {
      fetchData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tokenData?.invoice?.InvoiceId, invoiceData?.paymentHistory, loader]);

  // Update formik amount when invoiceData changes (after initial load)
  useEffect(() => {
    if (invoiceData && (invoiceData?.invoiceAccount || invoiceData?.Total)) {
      const amount = invoiceData?.invoiceAccount || invoiceData?.Total;
      if (amount && paymentFormik.values.amount !== amount.toString()) {
        paymentFormik.setFieldValue("amount", amount);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [invoiceData?.invoiceAccount, invoiceData?.Total]);

  // Helper function to check if invoice is paid
  const isInvoicePaid = (invoiceAccount) => {
    if (!invoiceAccount) return false;
    const accountValue = parseFloat(invoiceAccount);
    // Use tolerance check for floating-point precision issues (same as backend)
    return Math.abs(accountValue) < 0.01;
  };

  // Calculate card fee and totals (Cash Discount and Surcharge logic, NO tax)
  // Respects customer-level TransactionFee setting
  const calculatePaymentTotals = (
    amountValue = null,
    paymentMethodValue = null
  ) => {
    // Use provided values or fall back to formik values
    const amount =
      parseFloat(
        amountValue !== null ? amountValue : paymentFormik.values.amount
      ) || 0;
    const method =
      paymentMethodValue !== null ? paymentMethodValue : paymentMethod;

    let convenienceFee = 0;
    let cashPrice = amount;
    let cardPrice = amount;
    let finalTotal = amount;

    // Check if payment method is card and customer has TransactionFee enabled
    if (
      invoiceData?.customer?.TransactionFee &&
      method === "Credit/debit card" &&
      cardFeeConfig?.FeeAmount > 0
    ) {
      const pricingStructure =
        cardFeeConfig?.PricingStructure || "Cash Discount";

      if (pricingStructure === "Surcharge") {
        // SURCHARGE METHOD:
        // 1. Calculate convenience fee on subtotal
        if (cardFeeConfig.FeeType === "Percentage") {
          convenienceFee = (amount * cardFeeConfig.FeeAmount) / 100;
        } else if (cardFeeConfig.FeeType === "Fixed") {
          convenienceFee = cardFeeConfig.FeeAmount;
        }

        // 2. Total = subtotal + convenience fee (NO tax)
        finalTotal = amount + convenienceFee;
        cardPrice = amount + convenienceFee;
    } else {
        // CASH DISCOUNT METHOD:
        // 1. Calculate convenience fee on subtotal (cash price)
        if (cardFeeConfig.FeeType === "Percentage") {
          convenienceFee = (amount * cardFeeConfig.FeeAmount) / 100;
        } else if (cardFeeConfig.FeeType === "Fixed") {
          convenienceFee = cardFeeConfig.FeeAmount;
        }

        // 2. Card price = cash price + convenience fee
        cardPrice = amount + convenienceFee;

        // 3. Total = card price (NO tax)
        finalTotal = cardPrice;
      }
    }

    return {
      subtotal: amount,
      tax: 0, // No tax in invoice payment
      cashTotal: amount,
      convenienceFee: convenienceFee,
      cashPrice: cashPrice,
      cardPrice: cardPrice,
      cardTotal: finalTotal,
      finalTotal: finalTotal,
      showConvenienceFee:
        invoiceData?.customer?.TransactionFee &&
        method === "Credit/debit card" &&
        cardFeeConfig?.FeeAmount > 0,
      isCashDiscount:
        invoiceData?.customer?.TransactionFee &&
        method === "Credit/debit card" &&
        cardFeeConfig?.PricingStructure === "Cash Discount",
      isSurcharge:
        invoiceData?.customer?.TransactionFee &&
        method === "Credit/debit card" &&
        cardFeeConfig?.PricingStructure === "Surcharge",
    };
  };

  useEffect(() => {
    const updateError = () => {
      if (error) {
        if (
          paymentFormik?.values?.date &&
          (paymentFormik?.values?.method === "ACH bank payment" ||
            paymentFormik?.values?.method === "Credit/debit card") &&
          paymentFormik?.values?.billing_id
        ) {
          setError("");
        } else {
          if (paymentFormik?.values?.method === "Credit/debit card") {
            setError("Please select card");
          } else if (paymentFormik?.values?.method === "ACH bank payment") {
            setError("Please select ACH account");
          } else if (
            paymentFormik?.values?.method === "Apple Pay" ||
            paymentFormik?.values?.method === "Google Pay"
          ) {
            setError("");
          } else {
            setError("");
          }
        }
      }
    };
    updateError();
  }, [paymentFormik]);


  const [open, setOpen] = useState(false);
  const [payment, setPayment] = useState({});

  const handleClose = () => {
    setOpen(false);
  };

  const handlePaymentMethodChange = (method) => {
    setPaymentMethod(method);
    paymentFormik.setFieldValue("method", method);

    // Clear card selection when switching payment methods
    if (method !== "Credit/debit card") {
      setSelectedCard(undefined);
      paymentFormik.setFieldValue("billing_id", "");
      paymentFormik.setFieldValue("customer_vault_id", "");
      paymentFormik.setFieldValue("token", "");
    }
  };

  // Flags from API (gateway-keys IsDelete: false): hide Card/ACH when disabled - must be before useEffect that uses them
  const creditCardEnabled = tokenData?.creditCardEnabled !== false;
  const achEnabled = tokenData?.achEnabled === true;

  // When tokenData loads, if current payment method is hidden by flags, switch to first available
  useEffect(() => {
    if (!tokenData?.invoice) return;
    if (paymentMethod !== "Credit/debit card" || creditCardEnabled) return;
    const showAppleGoogle = tokenData.gatewayKeys !== "DEJAVOO" && tokenData.gatewayKeys !== "PAYA";
    const firstAvailable = showAppleGoogle ? "Apple Pay" : "Credit/debit card";
    if (firstAvailable !== "Credit/debit card") {
      setPaymentMethod(firstAvailable);
      paymentFormik.setFieldValue("method", firstAvailable);
      setSelectedCard(undefined);
      paymentFormik.setFieldValue("billing_id", "");
      paymentFormik.setFieldValue("customer_vault_id", "");
      paymentFormik.setFieldValue("token", "");
    }
  }, [tokenData?.invoice?.InvoiceId, creditCardEnabled]);

  const handleCardChange = (newCard) => {
    setSelectedCard(newCard);
  };

  const handleAmountChange = (e) => {
    const value = e.target.value.replace(/[^0-9.]/g, "");
    if ((value.match(/\./g) || []).length > 1) {
      return;
    }
    paymentFormik?.setFieldValue("amount", value);
  };

  // Get donation mode and logo from tokenData
  const isDonationMode = tokenData?.company?.IsDonationMode || false;
  const logo = tokenData?.companyProfile?.Logo || AppLogo;

  if (loader)
    return (
      <Grid
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh",
          backgroundColor: "#f5f7fa",
        }}
      >
        <LoaderComponent variant="skeleton" loader={loader} height="50" width="50" />
      </Grid>
    );
  else if (error) {
    return (
      <Grid
        style={{
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh",
          backgroundColor: "#f5f7fa",
        }}
      >
        <Typography
          variant="h5"
          style={{ color: "var(--bg-red-color)", marginBottom: "12px" }}
        >
          {error}
        </Typography>
        <Typography style={{ color: "#64748b" }}>
          Please contact support if you need assistance.
        </Typography>
      </Grid>
    );
  } else
    return (
      <>
        {isRefreshing && (
          <div
            style={{
              position: "fixed",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: "rgba(0, 0, 0, 0.6)",
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
              alignItems: "center",
              zIndex: 9999,
            }}
          >
            <LoaderComponent variant="skeleton" loader={isRefreshing} height="50" width="50" />
            <Typography
              style={{
                color: "white",
                marginTop: "20px",
                fontSize: "16px",
                fontWeight: 500,
              }}
            >
              Processing payment...
            </Typography>
          </div>
        )}
        <Grid
          style={{
            minHeight: "100vh",
            backgroundColor: "#f5f7fa",
          }}
        >
          {/* Modern Header */}
          <Grid
            style={{
              width: "100%",
              backgroundColor: isDonationMode
                ? "var(--bg-white-donor-color)"
                : "var(--bg-blue-color)",
              padding: "24px 0",
            }}
          >
            <Grid
              style={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                maxWidth: "1200px",
                margin: "0 auto",
                padding: "0 40px",
              }}
            >
              <img
                src={logo}
                alt="Company Logo"
                style={{
                  height: isDonationMode ? "60px" : "45px",
                  width: "auto",
                  maxWidth: "250px",
                  backgroundColor: isDonationMode ? "none" : "transparent",
                  padding: isDonationMode ? "8px 16px" : "0",
                }}
              />
            </Grid>
          </Grid>

          {/* Main Content - Modern Layout */}
          <Grid
            style={{
              maxWidth: "1000px",
              margin: "60px auto",
              padding: "0 40px",
            }}
          >
            {!tokenData ? (
              <div
                style={{
                  backgroundColor: "var(--bg-white-color)",
                  border: "1px solid #e2e8f0",
                  padding: "32px",
                }}
              >
                <Typography
                  style={{
                    color: "var(--text-blue-color)",
                    fontSize: "22px",
                    fontWeight: 500,
                    marginBottom: "24px",
                  }}
                >
                  Please enter your 8-digit access code to proceed
                </Typography>
                <InputText
                  label="Invoice Code"
                  fullWidth
                  value={invoiceCode}
                  onChange={(e) => setInvoiceCode(e.target.value)}
                  style={{ marginBottom: "20px" }}
                  fieldHeight="44px"
                />
                <Button
                  onClick={handleTokenSubmit}
                  disabled={!invoiceCode || invoiceCodeLoading}
                  style={{
                    backgroundColor: "var(--bg-blue-color)",
                    color: "var(--text-white-color)",
                    border: "none",
                    padding: "12px 24px",
                    fontSize: "14px",
                    fontWeight: 500,
                    textTransform: "none",
                    width: "100%",
                  }}
                >
                  {invoiceCodeLoading ? (
                    <CircularProgress size={20} color="inherit" />
                  ) : (
                    "Submit"
                  )}
                </Button>
              </div>
            ) : !isInvoicePaid(invoiceData?.invoiceAccount) ? (
              <div>
                {/* Simple Title Section */}
                <div style={{ marginBottom: "32px" }}>
                  <Typography
                    style={{
                      color: isDonationMode
                        ? "var(--border-donor-color)"
                        : "var(--text-blue-color)",
                      fontSize: "22px",
                      fontWeight: 500,
                      marginBottom: "6px",
                    }}
                  >
                    Complete Your Payment
                  </Typography>
                  <Typography
                    style={{
                      color: "#64748b",
                      fontSize: "14px",
                      fontWeight: 400,
                    }}
                  >
                    Invoice #{invoiceData?.InvoiceNumber} -{" "}
                    {invoiceData?.Subject}
                  </Typography>
                </div>

                {/* Simple Payment Summary */}
                <div
                  style={{
                    backgroundColor: "var(--bg-white-color)",
                    border: "1px solid #e2e8f0",
                    padding: "20px",
                    marginBottom: "24px",
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      marginBottom: "16px",
                    }}
                  >
                    <div>
                      <Typography
                        style={{
                          color: "#64748b",
                          fontSize: "12px",
                          fontWeight: 400,
                          marginBottom: "4px",
                        }}
                      >
                        Customer Name
                      </Typography>
                      <Typography
                        style={{
                          color: isDonationMode
                            ? "var(--border-donor-color)"
                            : "var(--text-blue-color)",
                          fontSize: "14px",
                          fontWeight: 500,
                        }}
                      >
                        {invoiceData?.customer?.FirstName}{" "}
                        {invoiceData?.customer?.LastName}
                      </Typography>
                    </div>

                    <div>
                      <Typography
                        style={{
                          color: "#64748b",
                          fontSize: "12px",
                          fontWeight: 400,
                          marginBottom: "4px",
                        }}
                      >
                        Invoice Amount
                      </Typography>
                      <Typography
                        style={{
                          color: isDonationMode
                            ? "var(--border-donor-color)"
                            : "var(--text-blue-color)",
                          fontSize: "14px",
                          fontWeight: 500,
                        }}
                      >
                        ${new Intl.NumberFormat("en-US", {
                          minimumFractionDigits: 2,
                          maximumFractionDigits: 2,
                        }).format(
                          parseFloat(invoiceData?.invoiceAccount || 0)
                        )}
                      </Typography>
                    </div>
                  </div>

                  <div
                    style={{
                      paddingTop: "16px",
                      borderTop: "1px solid #e2e8f0",
                    }}
                  >
                    <Typography
                      style={{
                        color: "#64748b",
                        fontSize: "12px",
                        fontWeight: 400,
                        marginBottom: "4px",
                      }}
                    >
                      Payment Amount
                    </Typography>
                    <Typography
                      style={{
                        color: isDonationMode
                          ? "var(--border-donor-color)"
                          : "var(--text-blue-color)",
                        fontSize: "20px",
                        fontWeight: 600,
                      }}
                    >
                      ${new Intl.NumberFormat("en-US", {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2,
                      }).format(parseFloat(paymentFormik?.values?.amount || 0))}
                    </Typography>
                  </div>
                </div>

                {/* Payment Method Selection */}
                <div style={{ marginBottom: "24px" }}>
                  <div
                    style={{
                      marginBottom: "16px",
                    }}
                  >
                    <Typography
                      style={{
                        color: isDonationMode
                          ? "var(--border-donor-color)"
                          : "var(--text-blue-color)",
                        fontWeight: "500",
                        fontSize: "14px",
                      }}
                    >
                      Payment Method
                    </Typography>
                  </div>

                            <div
                              style={{
                                display: "grid",
                                gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
                                gap: "12px",
                              }}
                            >
                              {tokenData.gatewayKeys !== "DEJAVOO" &&
                                tokenData.gatewayKeys !== "PAYA" && (
                                  <Button
                                    onClick={() =>
                                      handlePaymentMethodChange("Apple Pay")
                                    }
                                    style={{
                                      width: "100%",
                                      padding: "14px 16px",
                                      border:
                                        paymentMethod === "Apple Pay"
                                          ? `2px solid ${
                                              isDonationMode
                                                ? "var(--border-donor-color)"
                                                : "var(--border-blue-color)"
                                            }`
                                          : "1px solid #e2e8f0",
                                      background:
                                        paymentMethod === "Apple Pay"
                                          ? "#f8fafc"
                                          : "var(--bg-white-color)",
                                      color:
                                        paymentMethod === "Apple Pay"
                                          ? isDonationMode
                                            ? "var(--border-donor-color)"
                                            : "var(--text-blue-color)"
                                          : "#64748b",
                                      fontWeight:
                                        paymentMethod === "Apple Pay" ? "600" : "400",
                                      fontSize: "14px",
                                      display: "flex",
                                      alignItems: "center",
                                      justifyContent: "center",
                                      gap: "8px",
                                      textTransform: "none",
                                    }}
                                  >
                                    <svg
                                      width="20"
                                      height="20"
                                      viewBox="0 0 24 24"
                                      fill="currentColor"
                                    >
                                      <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.81-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z" />
                                    </svg>
                                    Pay with Apple Pay
                                  </Button>
                                )}
                              {tokenData.gatewayKeys !== "DEJAVOO" &&
                                tokenData.gatewayKeys !== "PAYA" && (
                                  <Button
                                    onClick={() =>
                                      handlePaymentMethodChange("Google Pay")
                                    }
                                    style={{
                                      width: "100%",
                                      padding: "14px 16px",
                                      border:
                                        paymentMethod === "Google Pay"
                                          ? `2px solid ${
                                              isDonationMode
                                                ? "var(--border-donor-color)"
                                                : "var(--border-blue-color)"
                                            }`
                                          : "1px solid #e2e8f0",
                                      background:
                                        paymentMethod === "Google Pay"
                                          ? "#f8fafc"
                                          : "var(--bg-white-color)",
                                      color:
                                        paymentMethod === "Google Pay"
                                          ? isDonationMode
                                            ? "var(--border-donor-color)"
                                            : "var(--text-blue-color)"
                                          : "#64748b",
                                      fontWeight:
                                        paymentMethod === "Google Pay" ? "600" : "400",
                                      fontSize: "14px",
                                      display: "flex",
                                      alignItems: "center",
                                      justifyContent: "center",
                                      gap: "8px",
                                      textTransform: "none",
                                    }}
                                  >
                                    <svg
                                      width="20"
                                      height="20"
                                      viewBox="0 0 24 24"
                                      fill="currentColor"
                                    >
                                      <path
                                        d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                                        fill="#4285F4"
                                      />
                                      <path
                                        d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                                        fill="#34A853"
                                      />
                                      <path
                                        d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                                        fill="#FBBC05"
                                      />
                                      <path
                                        d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                                        fill="#EA4335"
                                      />
                                    </svg>
                                    Pay with Google Pay
                                  </Button>
                                )}
                              <Button
                                disabled={!creditCardEnabled}
                                onClick={() =>
                                  creditCardEnabled && handlePaymentMethodChange("Credit/debit card")
                                }
                                style={{
                                  width: "100%",
                                  padding: "14px 16px",
                                  border:
                                    paymentMethod === "Credit/debit card"
                                      ? `2px solid ${
                                          isDonationMode
                                            ? "var(--border-donor-color)"
                                            : "var(--border-blue-color)"
                                        }`
                                      : "1px solid #e2e8f0",
                                  background:
                                    paymentMethod === "Credit/debit card"
                                      ? "#f8fafc"
                                      : "var(--bg-white-color)",
                                  color:
                                    paymentMethod === "Credit/debit card"
                                      ? isDonationMode
                                        ? "var(--border-donor-color)"
                                        : "var(--text-blue-color)"
                                      : "#64748b",
                                  fontWeight:
                                    paymentMethod === "Credit/debit card"
                                      ? "600"
                                      : "400",
                                  fontSize: "14px",
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  gap: "8px",
                                  textTransform: "none",
                                  opacity: creditCardEnabled ? 1 : 0.6,
                                  cursor: creditCardEnabled ? "pointer" : "not-allowed",
                                }}
                              >
                                <span style={{ display: "flex", alignItems: "center", justifyContent: "space-between", width: "100%", gap: 8, flexWrap: "wrap" }}>
                                  <span style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                                    <svg
                                      width="20"
                                      height="20"
                                      viewBox="0 0 24 24"
                                      fill="currentColor"
                                    >
                                      <path d="M20 4H4c-1.11 0-1.99.89-1.99 2L2 18c0 1.11.89 2 2 2h16c1.11 0 2-.89 2-2V6c0-1.11-.89-2-2-2zm0 14H4v-6h16v6zm0-10H4V6h16v2z" />
                                    </svg>
                                    Pay with Card on File
                                  </span>
                                  {!creditCardEnabled && (
                                    <span style={{ fontSize: "11px", color: "#B45309", fontWeight: 500 }}>
                                      For this company, credit/debit card is disabled.
                                    </span>
                                  )}
                                </span>
                              </Button>
                            </div>
                          </div>

                          {/* Amount Input */}
                          <div style={{ marginBottom: "24px" }}>
                            <DollerInput
                                value={paymentFormik?.values?.amount}
                                onChange={handleAmountChange}
                                onBlur={paymentFormik?.handleBlur}
                                error={
                                  paymentFormik?.touched?.amount &&
                                  Boolean(paymentFormik?.errors?.amount)
                                }
                                helperText={
                                  paymentFormik?.touched?.amount &&
                                  paymentFormik?.errors?.amount
                                }
                                InputLabelProps={{
                                  shrink:
                                    Boolean(paymentFormik?.values?.amount) ||
                                    paymentFormik?.touched?.amount,
                                }}
                                name="amount"
                                label="Amount"
                                type="text"
                                className="text-blue-color w-100 paymentApplied_amout"
                                fieldHeight="44px"
                                placeholder="Amount"
                                defaultValue={invoiceData?.invoiceAccount}
                              />
                          </div>

                          {paymentMethod === "Credit/debit card" && creditCardEnabled && (
                            <div className="mb-4">
                              <>
                                {!tokenData ? (
                                  <div>Loading company data...</div>
                                ) : !tokenData.gatewayKeys ? (
                                  <div>
                                    Error: No payment gatewayType configured
                                  </div>
                                ) : tokenData?.gatewayKeys === "PAYA" ? (
                                  <PayaAddCardForm
                                    onSuccess={(cardData) => {
                                      setSelectedCard(cardData);
                                    }}
                                    onCancel={() => {
                                      // Handle cancel
                                    }}
                                    CustomerId={invoiceData?.CustomerId}
                                    CompanyId={tokenData?.CompanyId}
                                  />
                                ) : (
                                  <CardPayment
                                    paymentFormik={paymentFormik}
                                    selectedCard={selectedCard}
                                    setSelectedCard={handleCardChange}
                                    CustomerId={tokenData?.customer?.UserId}
                                    CompanyId={tokenData?.company?.CompanyId}
                                    tokenDecode={{
                                      Gateway: tokenData?.gatewayKeys,
                                      CompanyId: tokenData?.company?.CompanyId,
                                    }}
                                  />
                                )}
                              </>
                            </div>
                          )}
                          {tokenData?.gatewayKeys !== "DEJAVOO" &&
                            tokenData?.gatewayKeys !== "PAYA" && (
                              <>
                                {paymentMethod === "Apple Pay" && (
                                  <div className="mb-4">
                                    <ApplePayPayment
                                      paymentFormik={paymentFormik}
                                      invoiceData={invoiceData}
                                      CompanyUrl={
                                        tokenData?.company?.CompanyUrl
                                      }
                                      CustomerId={tokenData?.customer?.UserId}
                                      CompanyId={tokenData?.company?.CompanyId}
                                    />
                                  </div>
                                )}
                                {paymentMethod === "Google Pay" && (
                                  <div className="mb-4">
                                    <GooglePayPayment
                                      paymentFormik={paymentFormik}
                                      CustomerId={tokenData?.customer?.UserId}
                                    />
                                  </div>
                                )}
                              </>
                            )}

                          {error && (
                            <Typography
                              style={{
                                color: "#dc2626",
                                fontSize: "14px",
                                marginBottom: "24px",
                              }}
                            >
                              {error}
                            </Typography>
                          )}

                          {/* Fee Breakdown */}
                          {paymentMethod === "Credit/debit card" &&
                            paymentFormik?.values?.amount &&
                            parseFloat(paymentFormik?.values?.amount) > 0 &&
                            (() => {
                              const totals = calculatePaymentTotals();
                              return (
                                totals.showConvenienceFee &&
                                totals.convenienceFee > 0 && (
                                  <div
                                    style={{
                                      border: "1px solid #e2e8f0",
                                      padding: "20px",
                                      marginBottom: "24px",
                                    }}
                                  >
                                    <Typography
                                      style={{
                                        color: isDonationMode
                                          ? "var(--border-donor-color)"
                                          : "var(--text-blue-color)",
                                        fontSize: "14px",
                                        fontWeight: 500,
                                        marginBottom: "16px",
                                      }}
                                    >
                                      Fee Breakdown
                                    </Typography>
                                    {totals.isCashDiscount ? (
                                      <>
                                        <div
                                          style={{
                                            display: "flex",
                                            justifyContent: "space-between",
                                            marginBottom: "12px",
                                          }}
                                        >
                                          <Typography
                                            style={{
                                              color: "#64748b",
                                              fontSize: "13px",
                                              fontWeight: 400,
                                            }}
                                          >
                                            Subtotal:
                                          </Typography>
                                          <Typography
                                            style={{
                                              color: isDonationMode
                                                ? "var(--border-donor-color)"
                                                : "var(--text-blue-color)",
                                              fontSize: "13px",
                                              fontWeight: 400,
                                            }}
                                          >
                                            ${totals.cashPrice.toFixed(2)}
                                          </Typography>
                                        </div>
                                        <div
                                          style={{
                                            display: "flex",
                                            justifyContent: "space-between",
                                            marginBottom: "12px",
                                          }}
                                        >
                                          <Typography
                                            style={{
                                              color: "#64748b",
                                              fontSize: "13px",
                                              fontWeight: 400,
                                            }}
                                          >
                                            {cardFeeConfig?.FeeLabel ||
                                              "Convenience Fee"}:
                                          </Typography>
                                          <Typography
                                            style={{
                                              color: isDonationMode
                                                ? "var(--border-donor-color)"
                                                : "var(--text-blue-color)",
                                              fontSize: "13px",
                                              fontWeight: 400,
                                            }}
                                          >
                                            ${totals.convenienceFee.toFixed(2)}
                                          </Typography>
                                        </div>
                                      </>
                                    ) : (
                                      <>
                                        <div
                                          style={{
                                            display: "flex",
                                            justifyContent: "space-between",
                                            marginBottom: "12px",
                                          }}
                                        >
                                          <Typography
                                            style={{
                                              color: "#64748b",
                                              fontSize: "13px",
                                              fontWeight: 400,
                                            }}
                                          >
                                            Subtotal:
                                          </Typography>
                                          <Typography
                                            style={{
                                              color: isDonationMode
                                                ? "var(--border-donor-color)"
                                                : "var(--text-blue-color)",
                                              fontSize: "13px",
                                              fontWeight: 400,
                                            }}
                                          >
                                            ${totals.subtotal.toFixed(2)}
                                          </Typography>
                                        </div>
                                        {totals.showConvenienceFee &&
                                          totals.isSurcharge && (
                                            <div
                                              style={{
                                                display: "flex",
                                                justifyContent: "space-between",
                                                marginBottom: "12px",
                                              }}
                                            >
                                              <Typography
                                                style={{
                                                  color: "#64748b",
                                                  fontSize: "13px",
                                                  fontWeight: 400,
                                                }}
                                              >
                                                {cardFeeConfig?.FeeLabel ||
                                                  "Convenience Fee"}:
                                              </Typography>
                                              <Typography
                                                style={{
                                                  color: isDonationMode
                                                    ? "var(--border-donor-color)"
                                                    : "var(--text-blue-color)",
                                                  fontSize: "13px",
                                                  fontWeight: 400,
                                                }}
                                              >
                                                ${totals.convenienceFee.toFixed(
                                                  2
                                                )}
                                              </Typography>
                                            </div>
                                          )}
                                      </>
                                    )}
                                    <div
                                      style={{
                                        display: "flex",
                                        justifyContent: "space-between",
                                        paddingTop: "16px",
                                        borderTop: "1px solid #e2e8f0",
                                        marginTop: "12px",
                                      }}
                                    >
                                      <Typography
                                        style={{
                                          color: isDonationMode
                                            ? "var(--border-donor-color)"
                                            : "var(--text-blue-color)",
                                          fontSize: "15px",
                                          fontWeight: 600,
                                        }}
                                      >
                                        Total:
                                      </Typography>
                                      <Typography
                                        style={{
                                          color: isDonationMode
                                            ? "var(--border-donor-color)"
                                            : "var(--text-blue-color)",
                                          fontSize: "15px",
                                          fontWeight: 600,
                                        }}
                                      >
                                        ${(() => {
                                          const displayedSubtotal = parseFloat(
                                            totals.subtotal.toFixed(2)
                                          );
                                          const displayedConvenienceFee =
                                            parseFloat(
                                              totals.convenienceFee.toFixed(2)
                                            );
                                          const displayedTotal =
                                            totals.isCashDiscount
                                              ? parseFloat(
                                                  totals.cashPrice.toFixed(2)
                                                ) + displayedConvenienceFee
                                              : displayedSubtotal +
                                                displayedConvenienceFee;
                                          return displayedTotal.toFixed(2);
                                        })()}
                                      </Typography>
                                    </div>
                                  </div>
                                )
                              );
                            })()}

                          {/* Pay Button */}
                          {paymentMethod === "Credit/debit card" && (
                            <Button
                              onClick={() => {
                                paymentFormik?.handleSubmit();
                              }}
                              disabled={buttonLoader}
                              style={{
                                width: "100%",
                                backgroundColor: isDonationMode
                                  ? "var(--border-donor-color)"
                                  : "var(--bg-blue-color)",
                                color: "var(--text-white-color)",
                                border: "none",
                                padding: "12px 24px",
                                fontSize: "14px",
                                fontWeight: 600,
                                textTransform: "none",
                                marginBottom: "24px",
                              }}
                            >
                              {buttonLoader ? (
                                <CircularProgress size={20} color="inherit" />
                              ) : paymentFormik?.values?.amount &&
                                parseFloat(paymentFormik?.values?.amount) > 0
                              ? (() => {
                                  const totals = calculatePaymentTotals();
                                  if (
                                    totals.showConvenienceFee &&
                                    totals.convenienceFee > 0
                                  ) {
                                    const displayedSubtotal = parseFloat(
                                      totals.subtotal.toFixed(2)
                                    );
                                    const displayedConvenienceFee = parseFloat(
                                      totals.convenienceFee.toFixed(2)
                                    );
                                    const displayedTotal = totals.isCashDiscount
                                      ? parseFloat(
                                          totals.cashPrice.toFixed(2)
                                        ) + displayedConvenienceFee
                                      : displayedSubtotal +
                                        displayedConvenienceFee;
                                    return `Pay $${displayedTotal.toFixed(2)}`;
                                  }
                                  return "Pay";
                                })()
                              : "Pay"}
                            </Button>
                          )}

                          {/* Company Contact Info Footer */}
                          {tokenData && (
                            <div
                              style={{
                                paddingTop: "32px",
                                borderTop: "1px solid #e2e8f0",
                                padding: "24px",
                                margin: "0 -40px -40px -40px",
                              }}
                            >
                              <Typography
                                style={{
                                  color: isDonationMode
                                    ? "var(--border-donor-color)"
                                    : "var(--text-blue-color)",
                                  fontSize: "14px",
                                  fontWeight: 500,
                                  marginBottom: "12px",
                                }}
                              >
                                {tokenData?.companyProfile?.CompanyName ||
                                  "Company Name"}
                              </Typography>
                              {tokenData?.companyProfile?.PhoneNumber && (
                                <Typography
                                  style={{
                                    color: "#64748b",
                                    fontSize: "12px",
                                    fontWeight: 400,
                                    marginBottom: "4px",
                                  }}
                                >
                                  {formatUSPhone(tokenData?.companyProfile?.PhoneNumber)}
                                </Typography>
                              )}
                              {tokenData?.company?.EmailAddress && (
                                <Typography
                                  style={{
                                    color: "#64748b",
                                    fontSize: "12px",
                                    fontWeight: 400,
                                  }}
                                >
                                  {tokenData?.company?.EmailAddress}
                                </Typography>
                              )}
                            </div>
                          )}
                        </div>
            ) : (
              <div
                style={{
                  maxWidth: "800px",
                  margin: "0 auto",
                  padding: "0 40px",
                }}
              >
                {/* Customer Details Card */}
                <div
                  style={{
                    backgroundColor: "var(--bg-white-color)",
                    border: "1px solid #e2e8f0",
                    padding: "32px",
                    position: "relative",
                    overflow: "hidden",
                  }}
                >
                  {/* Paid Watermark */}
                  <div
                    style={{
                      position: "absolute",
                      top: "50%",
                      left: "50%",
                      transform: "translate(-50%, -50%) rotate(-45deg)",
                      color: isDonationMode
                        ? "rgba(41, 179, 226, 0.15)"
                        : "rgba(8, 159, 87, 0.15)",
                      fontSize: "72px",
                      fontWeight: 900,
                      zIndex: 1,
                      letterSpacing: "12px",
                      whiteSpace: "nowrap",
                      userSelect: "none",
                      pointerEvents: "none",
                    }}
                  >
                    PAID
                  </div>

                  {/* Content */}
                  <div style={{ position: "relative", zIndex: 2 }}>
                    {/* Title */}
                    <Typography
                      style={{
                        color: isDonationMode
                          ? "var(--border-donor-color)"
                          : "var(--text-blue-color)",
                        fontSize: "22px",
                        fontWeight: 500,
                        marginBottom: "24px",
                      }}
                    >
                      Customer Details
                    </Typography>

                    {/* Customer Name */}
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        marginBottom: "16px",
                      }}
                    >
                      <Typography
                        style={{
                          color: isDonationMode
                            ? "var(--border-donor-color)"
                            : "var(--text-blue-color)",
                          fontSize: "14px",
                          fontWeight: 500,
                        }}
                      >
                        Customer Name
                      </Typography>
                      <Typography
                        style={{
                          color: "#64748b",
                          fontSize: "14px",
                          fontWeight: 400,
                        }}
                      >
                        {(() => {
                          const firstName =
                            invoiceData?.customer?.FirstName ||
                            tokenData?.customer?.FirstName ||
                            "";
                          const lastName =
                            invoiceData?.customer?.LastName ||
                            tokenData?.customer?.LastName ||
                            "";
                          const fullName = `${firstName} ${lastName}`.trim();
                          return fullName || "-";
                        })()}
                      </Typography>
                    </div>

                    {/* Property Address */}
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        marginBottom: "16px",
                      }}
                    >
                      <Typography
                        style={{
                          color: isDonationMode
                            ? "var(--border-donor-color)"
                            : "var(--text-blue-color)",
                          fontSize: "14px",
                          fontWeight: 500,
                        }}
                      >
                        Property Address
                      </Typography>
                      <Typography
                        style={{
                          color: "#64748b",
                          fontSize: "14px",
                          fontWeight: 400,
                          textAlign: "right",
                          maxWidth: "60%",
                        }}
                      >
                        {(() => {
                          const location =
                            invoiceData?.location || tokenData?.invoice?.location;
                          if (location) {
                            const addressParts = Object.entries(location)
                              .filter(
                                ([key, value]) =>
                                  key !== "Country" && value
                              )
                              .map(([key, value]) => value);
                            return addressParts.length > 0
                              ? addressParts.join(", ")
                              : "-";
                          }
                          return "-";
                        })()}
                      </Typography>
                    </div>

                    {/* Invoice Number */}
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        marginBottom: "16px",
                      }}
                    >
                      <Typography
                        style={{
                          color: isDonationMode
                            ? "var(--border-donor-color)"
                            : "var(--text-blue-color)",
                          fontSize: "14px",
                          fontWeight: 500,
                        }}
                      >
                        Invoice Number
                      </Typography>
                      <Typography
                        style={{
                          color: "#64748b",
                          fontSize: "14px",
                          fontWeight: 400,
                        }}
                      >
                        #{invoiceData?.InvoiceNumber ||
                          tokenData?.invoice?.InvoiceNumber ||
                          "-"}
                      </Typography>
                    </div>

                    {/* Invoice Title */}
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        marginBottom: "24px",
                      }}
                    >
                      <Typography
                        style={{
                          color: isDonationMode
                            ? "var(--border-donor-color)"
                            : "var(--text-blue-color)",
                          fontSize: "14px",
                          fontWeight: 500,
                        }}
                      >
                        Invoice Title
                      </Typography>
                      <Typography
                        style={{
                          color: "#64748b",
                          fontSize: "14px",
                          fontWeight: 400,
                        }}
                      >
                        {invoiceData?.Subject ||
                          tokenData?.invoice?.Subject ||
                          "-"}
                      </Typography>
                    </div>

                    {/* Divider */}
                    <div
                      style={{
                        height: "1px",
                        backgroundColor: "#e2e8f0",
                        marginBottom: "24px",
                      }}
                    />

                    {/* Total */}
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        marginBottom: "16px",
                      }}
                    >
                      <Typography
                        style={{
                          color: isDonationMode
                            ? "var(--border-donor-color)"
                            : "var(--text-blue-color)",
                          fontSize: "14px",
                          fontWeight: 600,
                        }}
                      >
                        Total
                      </Typography>
                      <Typography
                        style={{
                          color: isDonationMode
                            ? "var(--border-donor-color)"
                            : "var(--text-blue-color)",
                          fontSize: "14px",
                          fontWeight: 600,
                        }}
                      >
                        ${new Intl.NumberFormat("en-US", {
                          minimumFractionDigits: 2,
                          maximumFractionDigits: 2,
                        }).format(
                          invoiceData?.Total ||
                            tokenData?.invoice?.Total ||
                            0
                        )}
                      </Typography>
                    </div>

                    {/* Payment History */}
                    {invoiceData?.paymentHistory &&
                      invoiceData?.paymentHistory?.length > 0 && (
                        <div style={{ marginBottom: "16px" }}>
                          {invoiceData?.paymentHistory?.map((item, index) => (
                            <div
                              key={index}
                              style={{
                                display: "flex",
                                justifyContent: "space-between",
                                marginBottom: "12px",
                              }}
                            >
                              <Typography
                                style={{
                                  color: "#089F57",
                                  fontSize: "14px",
                                  fontWeight: 500,
                                  textDecoration: "underline",
                                  textDecorationColor: "#089F57",
                                }}
                              >
                                Payment
                              </Typography>
                              <Typography
                                style={{
                                  color: isDonationMode
                                    ? "var(--border-donor-color)"
                                    : "var(--text-blue-color)",
                                  fontSize: "14px",
                                  fontWeight: 400,
                                }}
                              >
                                {item?.date || "-"}
                              </Typography>
                              <Typography
                                style={{
                                  color: "#089F57",
                                  fontSize: "14px",
                                  fontWeight: 500,
                                }}
                              >
                                ${new Intl.NumberFormat("en-US", {
                                  minimumFractionDigits: 2,
                                  maximumFractionDigits: 2,
                                }).format(item?.amount ?? 0)}
                              </Typography>
                            </div>
                          ))}
                        </div>
                      )}

                    {/* Divider */}
                    <div
                      style={{
                        height: "1px",
                        backgroundColor: "#e2e8f0",
                        marginBottom: "16px",
                      }}
                    />

                    {/* Customer Balance */}
                    {/* <div
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        marginBottom: "16px",
                      }}
                    >
                      <Typography
                        style={{
                          color: isDonationMode
                            ? "var(--border-donor-color)"
                            : "var(--text-blue-color)",
                          fontSize: "14px",
                          fontWeight: 600,
                        }}
                      >
                        Customer Balance
                      </Typography>
                      <Typography
                        style={{
                          color: "#64748b",
                          fontSize: "14px",
                          fontWeight: 600,
                        }}
                      >
                        ${new Intl.NumberFormat("en-US", {
                          minimumFractionDigits: 2,
                          maximumFractionDigits: 2,
                        }).format(
                          invoiceData?.customerAccount ??
                            tokenData?.invoice?.Ledger?.Balance ??
                            0
                        )}
                      </Typography>
                    </div> */}

                    {/* Account Balance */}
                    {/* <div
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                      }}
                    >
                      <Typography
                        style={{
                          color: isDonationMode
                            ? "var(--border-donor-color)"
                            : "var(--text-blue-color)",
                          fontSize: "14px",
                          fontWeight: 600,
                        }}
                      >
                        Account Balance
                      </Typography>
                      <Typography
                        style={{
                          color: "#64748b",
                          fontSize: "14px",
                          fontWeight: 600,
                        }}
                      >
                        ${new Intl.NumberFormat("en-US", {
                          minimumFractionDigits: 2,
                          maximumFractionDigits: 2,
                        }).format(Math.abs(invoiceData?.invoiceAccount ?? 0))}
                      </Typography>
                    </div> */}
                  </div>
                </div>
              </div>
            )}
          </Grid>

          <Dialog open={open} onClose={handleClose}>
            <DialogTitle style={{ fontWeight: 700, fontSize: "30px" }}>
              Payment
            </DialogTitle>
            <DialogContent>
              <Grid className="my-3">
                <InputText
                  name="Title"
                  placeholder="Invoice #1..."
                  label="Applied to"
                  type="Title"
                  className="text-blue-color w-100"
                  fieldHeight="44px"
                  disable={true}
                  defaultValue={`${invoiceData?.Subject}`}
                />
              </Grid>
              <Grid className="my-3">
                <InputText
                  name="Title"
                  placeholder="09 Sept, 2024..."
                  label="Transaction Date"
                  type="Title"
                  className="text-blue-color w-100"
                  fieldHeight="44px"
                  disable={true}
                  defaultValue={`${paymentFormik?.values?.date}`}
                />
              </Grid>
              <Grid className="my-3">
                <InputText
                  name="Title"
                  placeholder="Cash..."
                  label="Method"
                  type="Title"
                  className="text-blue-color w-100"
                  fieldHeight="44px"
                  disable={true}
                  defaultValue={`${payment?.method}`}
                />
              </Grid>
              <Typography className="text-blue-colo heading-fourr">
                Info
              </Typography>
              <Grid className="my-3">
                <InputText
                  name="Title"
                  placeholder="80.00..."
                  label="Amount"
                  type="Title"
                  className="text-blue-color w-100"
                  fieldHeight="44px"
                  disable={true}
                  defaultValue={`${payment?.amount}`}
                />
              </Grid>
              <Grid className="my-3">
                <InputText
                  name="Title"
                  placeholder="Payment applied to invoice #1..."
                  label="Details"
                  type="Title"
                  className="text-blue-color w-100"
                  fieldHeight="44px"
                  disable={true}
                  defaultValue={`Payment applied to invoice #${invoiceData?.InvoiceNumber}`}
                />
              </Grid>
            </DialogContent>
            <DialogActions>
              <WhiteButton onClick={handleClose} label="Cancel" />
              <WhiteButton label="Download Pdf" />
              <BlueButton label="Email Receipt" />
            </DialogActions>
          </Dialog>
        </Grid>
      </>
    );
};

export default InvoiceLivePayment;
